import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Languages, Coins, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1709846486154-f9172678be6f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

const Feature = ({ icon: Icon, title, desc, i }) => (
  <motion.div {...fade(0.5 + i * 0.12)} className="gold-card p-7" data-testid={`landing-feature-${i}`}>
    <Icon className="text-[#E5C158] mb-5" size={22} strokeWidth={1.5} />
    <h3 className="font-serif text-2xl text-[#F4F6FA] mb-2">{title}</h3>
    <p className="text-sm text-[#8E9BB4] leading-relaxed">{desc}</p>
  </motion.div>
);

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const target = user ? (user.paid ? "/journal" : "/paywall") : "/auth?mode=register";

  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[760px] object-cover object-right opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060A12]/40 via-[#060A12]/80 to-[#060A12]" />
      </div>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-24 sm:pt-36 pb-24">
        <motion.p {...fade(0)} className="label-gold mb-6">{t("hero_kicker")}</motion.p>
        <motion.h1 {...fade(0.1)} className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#F4F6FA] leading-[1.02] max-w-3xl">
          {t("hero_title_1")}<br />
          <em className="text-gold-gradient not-italic font-serif italic">{t("hero_title_2")}</em>
        </motion.h1>
        <motion.p {...fade(0.2)} className="mt-8 text-base sm:text-lg text-[#C2C9DB] max-w-xl leading-relaxed">{t("hero_sub")}</motion.p>
        <motion.div {...fade(0.3)} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
          <Link to={target} data-testid="hero-cta-btn" className="btn-gold inline-flex items-center gap-2 px-7 py-4 rounded-full text-base w-fit">
            {t("hero_cta")} <ArrowRight size={18} />
          </Link>
          <span className="text-sm text-[#8E9BB4]" data-testid="hero-price-label">{t("hero_price")}</span>
        </motion.div>
      </section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-32 grid md:grid-cols-3 gap-5">
        <Feature i={0} icon={Lock} title={t("feat1_t")} desc={t("feat1_d")} />
        <Feature i={1} icon={Languages} title={t("feat2_t")} desc={t("feat2_d")} />
        <Feature i={2} icon={Coins} title={t("feat3_t")} desc={t("feat3_d")} />
      </section>
    </div>
  );
}
